import { type LandingSectionDefinition, type LandingSectionField } from "../../fields";
import { bikiniContinueCheckoutProps, bikiniContinueLandingProps, bikiniContinueThankYouProps } from "./templates";
import { type bikiniContinueSectionTypes } from "./types";

const group = "Hollywood po Bikini";

const text = (key: string, label: string, help?: string): LandingSectionField => ({
  key,
  label,
  type: "text",
  ...(help ? { help } : {}),
});
const textarea = (key: string, label: string): LandingSectionField => ({ key, label, type: "textarea" });
const richText = (key: string, label: string): LandingSectionField => ({
  key,
  label,
  type: "richText",
  help: "Nową linię wstawisz przez <br />, pogrubienie przez <strong>.",
});
const image = (key: string, label: string): LandingSectionField => ({ key, label, type: "image" });
const stringList = (key: string, label: string): LandingSectionField => ({ key, label, type: "stringList" });

const scrollTarget: LandingSectionField = {
  key: "scrollTargetId",
  label: "Sekcja, do której przewija przycisk",
  type: "text",
  placeholder: "checkout",
  help: "ID sekcji (domyślnie checkout, czyli końcowe wezwanie do zakupu).",
};

const landing = bikiniContinueLandingProps;
const checkout = bikiniContinueCheckoutProps;

export const bikiniContinueSectionDefinitions: Record<
  (typeof bikiniContinueSectionTypes)[number],
  LandingSectionDefinition
> = {
  bikiniContinueLandingEffects: {
    label: "Śledzenie landingu Hollywood po Bikini (niewidoczne)",
    group,
    fields: [
      text("stepSlug", "step_slug w zdarzeniu landing_viewed"),
      {
        key: "facebookPixel",
        label: "Piksel Facebooka Hollywood",
        type: "boolean",
        help: "Ten sam piksel co na landingu Hollywood po Bikini z kodu.",
      },
    ],
    defaultProps: landing.effects,
  },
  bikiniContinueHeader: {
    label: "Nagłówek z menu",
    group,
    fields: [
      {
        key: "menuItems",
        label: "Pozycje menu",
        type: "list",
        itemLabel: "Pozycja",
        fields: [text("label", "Etykieta"), text("href", "Link", "Np. #warunki, czyli ID sekcji.")],
      },
      text("ctaLabel", "Przycisk"),
      text("ctaHref", "Link przycisku"),
    ],
    defaultProps: landing.header,
  },
  bikiniContinueHero: {
    label: "Hero z wideo",
    group,
    fields: [
      text("badge", "Plakietka"),
      richText("title", "Tytuł"),
      richText("subtitle", "Podtytuł"),
      text("ctaLabel", "Przycisk"),
      scrollTarget,
      text("videoBadge", "Plakietka nad wideo"),
      text("wistiaVideoId", "ID wideo Wistia", "Np. ju4rv7b3kv."),
      {
        key: "stats",
        label: "Liczby pod wideo",
        type: "list",
        itemLabel: "Liczba",
        fields: [text("value", "Wartość"), text("label", "Opis")],
      },
      {
        key: "showTimer",
        label: "Licznik promocji",
        type: "boolean",
        help: "Pokazuje licznik, gdy funnel ma datę końca i licznik w ustawieniach kroku landingu.",
      },
    ],
    defaultProps: landing.hero,
  },
  bikiniContinueTransformations: {
    label: "Metamorfozy (karuzela)",
    group,
    fields: [],
    defaultProps: landing.transformations,
  },
  bikiniContinueReviews: {
    label: "Opinie Google z przyciskiem",
    group,
    fields: [text("ctaLabel", "Przycisk"), scrollTarget],
    defaultProps: landing.reviews,
  },
  bikiniContinueWhy: {
    label: "Dlaczego to działa",
    group,
    fields: [
      text("title", "Tytuł pierwszej karty"),
      text("intro", "Wstęp"),
      stringList("items", "Lista"),
      richText("outro", "Zakończenie"),
      text("problemTitle", "Tytuł drugiej karty"),
      richText("problemText", "Tekst drugiej karty"),
      text("needsIntro", "Wstęp do listy potrzeb"),
      stringList("needs", "Potrzeby ciała"),
      text("needsOutro", "Tekst po liście potrzeb"),
      text("durationTitle", "Pogrubione zdanie o czasie programu"),
      text("durationIntro", "Wstęp do drugiej listy"),
      stringList("durationItems", "Druga lista"),
      image("image", "Zdjęcie"),
      text("imageAlt", "Opis zdjęcia (alt)"),
    ],
    defaultProps: landing.why,
  },
  bikiniContinueCtaButton: {
    label: "Przycisk przewijający do zakupu",
    group,
    fields: [text("label", "Przycisk"), text("source", "source w zdarzeniu PostHog", "Np. middle."), scrollTarget],
    defaultProps: landing.middleCta,
  },
  bikiniContinueProgram: {
    label: "Program Hollywood to system",
    group,
    fields: [
      text("title", "Tytuł"),
      stringList("stats", "Liczby programu"),
      textarea("text", "Tekst pod liczbami"),
      text("benefitsTitle", "Tytuł listy korzyści"),
      stringList("benefits", "Korzyści"),
      textarea("closing", "Zakończenie"),
      image("image", "Zdjęcie"),
      text("imageAlt", "Opis zdjęcia (alt)"),
    ],
    defaultProps: landing.program,
  },
  bikiniContinueComparison: {
    label: "Porównanie kosztów i aplikacja",
    group,
    fields: [
      text("title", "Tytuł"),
      text("outsideEyebrow", "Nadtytuł lewej karty"),
      text("outsideTitle", "Tytuł lewej karty"),
      {
        key: "outsideRows",
        label: "Koszty poza programem",
        type: "list",
        itemLabel: "Koszt",
        fields: [text("label", "Opis"), text("price", "Kwota")],
      },
      text("outsideTotal", "Suma"),
      text("outsideTotalNote", "Dopisek pod sumą"),
      text("programEyebrow", "Nadtytuł prawej karty"),
      text("programPrice", "Cena programu"),
      stringList("programItems", "Zalety programu"),
      text("savingsTitle", "Oszczędność"),
      text("savingsNote", "Dopisek pod oszczędnością"),
      text("appTitle", "Tytuł bloku aplikacji"),
      richText("appText", "Tekst bloku aplikacji"),
      {
        key: "appScreens",
        label: "Zrzuty aplikacji",
        type: "list",
        itemLabel: "Zrzut",
        fields: [image("image", "Obraz"), text("alt", "Opis (alt)")],
      },
    ],
    defaultProps: landing.comparison,
  },
  bikiniContinueConditions: {
    label: "Warunki uczestniczki",
    group,
    fields: [
      text("title", "Tytuł"),
      text("standardLabel", "Standardowa cena: etykieta"),
      text("standardPrice", "Standardowa cena"),
      text("standardNote", "Standardowa cena: dopisek"),
      text("discountLabel", "Rabat: etykieta"),
      text("discountValue", "Rabat"),
      text("discountNote", "Rabat: dopisek"),
      text("yourPriceLabel", "Twoja cena: etykieta"),
      text("yourPrice", "Twoja cena"),
      text("yourPriceNote", "Twoja cena: dopisek"),
      textarea("intro", "Wstęp"),
      stringList("items", "Lista warunków"),
      text("highlightTitle", "Wyróżnienie: tytuł"),
      text("highlightText", "Wyróżnienie: tekst"),
      text("note", "Uwaga pod warunkami"),
      text("ctaLabel", "Przycisk"),
      scrollTarget,
    ],
    defaultProps: landing.conditions,
  },
  bikiniContinueGuarantee: {
    label: "Gwarancja i 2 możliwości",
    group,
    fields: [
      text("title", "Tytuł"),
      text("ifLabel", "Wstęp do warunków"),
      stringList("conditions", "Warunki gwarancji"),
      textarea("text", "Tekst gwarancji"),
      text("motto", "Hasło"),
      text("boxTitle", "Ramka: tytuł"),
      textarea("boxText", "Ramka: tekst"),
      text("optionsTitle", "Tytuł możliwości"),
      text("option1Badge", "Opcja 1: plakietka"),
      text("option1Title", "Opcja 1: tytuł"),
      text("option1Text", "Opcja 1: tekst"),
      text("option2Badge", "Opcja 2: plakietka"),
      text("option2Title", "Opcja 2: tytuł"),
      stringList("option2Items", "Opcja 2: lista"),
      text("option2Note", "Opcja 2: dopisek"),
      text("closing", "Zakończenie"),
    ],
    defaultProps: landing.guarantee,
  },
  bikiniContinueFinalCta: {
    label: "Końcowe wezwanie do zakupu",
    group,
    fields: [
      text("badge", "Plakietka"),
      text("title", "Tytuł"),
      text("subtitle", "Podtytuł"),
      richText("priceLine", "Linia z ceną"),
      text("ctaLabel", "Przycisk"),
      {
        key: "href",
        label: "Link przycisku",
        type: "url",
        help: "Puste: następny krok funnelu (checkout).",
      },
    ],
    defaultProps: landing.finalCta,
  },
  bikiniContinueFooter: {
    label: "Stopka",
    group,
    fields: [],
    defaultProps: landing.footer,
  },
  bikiniContinueCheckoutIntro: {
    label: "Checkout: nagłówek",
    group,
    fields: [text("eyebrow", "Nadtytuł"), textarea("title", "Tytuł"), text("subtitle", "Podtytuł")],
    defaultProps: checkout.intro,
  },
  bikiniContinueCheckoutPriceSummary: {
    label: "Checkout: podsumowanie ceny",
    group,
    fields: [
      text("title", "Tytuł"),
      {
        key: "rows",
        label: "Wiersze",
        type: "list",
        itemLabel: "Wiersz",
        fields: [
          text("label", "Etykieta"),
          text("value", "Wartość"),
          { key: "emphasized", label: "Większa, pogrubiona wartość", type: "boolean" },
        ],
      },
      text("highlightTitle", "Wyróżnienie: tytuł"),
      text("highlightText", "Wyróżnienie: tekst"),
    ],
    defaultProps: checkout.priceSummary,
  },
  bikiniContinueCheckoutBenefits: {
    label: "Checkout: co otrzymujesz",
    group,
    fields: [text("title", "Tytuł"), text("intro", "Wstęp"), stringList("items", "Lista")],
    defaultProps: checkout.benefits,
  },
  bikiniContinueCheckoutGuarantee: {
    label: "Checkout: gwarancja",
    group,
    fields: [text("title", "Tytuł"), textarea("text", "Tekst"), text("note", "Dopisek")],
    defaultProps: checkout.guarantee,
  },
  bikiniContinueCheckoutPayment: {
    label: "Checkout: formularz płatności",
    group: "Checkout",
    fields: [
      text("title", "Tytuł"),
      text("ctaText", "Przycisk płatności"),
      text("securityNote", "Dopisek o bezpieczeństwie"),
      text("costsNote", "Dopisek o kosztach"),
    ],
    defaultProps: checkout.payment,
  },
  bikiniContinueCheckoutFaq: {
    label: "Checkout: mini FAQ",
    group,
    fields: [
      text("title", "Tytuł"),
      {
        key: "items",
        label: "Pytania",
        type: "list",
        itemLabel: "Pytanie",
        fields: [text("question", "Pytanie"), textarea("answer", "Odpowiedź")],
      },
    ],
    defaultProps: checkout.faq,
  },
  bikiniContinueThankYou: {
    label: "Podziękowanie z podsumowaniem zamówienia",
    group: "Podziękowanie",
    fields: [
      text("eyebrow", "Nadtytuł"),
      text("title", "Tytuł"),
      textarea("description", "Opis"),
      text("renewalTitle", "Tytuł przy odnowieniu"),
      textarea("renewalDescription", "Opis przy odnowieniu"),
      text("stepSlug", "step_slug w zdarzeniu thankYou_page_viewed"),
    ],
    defaultProps: bikiniContinueThankYouProps,
  },
};
